import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  makeStyles,
  shorthands,
  tokens,
  Text,
  Link,
  Spinner,
} from '@fluentui/react-components';
import { Warning24Regular } from '@fluentui/react-icons';
import DataTable, { ColumnConfig } from '../components/DataTable';
import { queryKeys } from '../lib/queryClient';
import {
  fetchAuthnetReviewCustomers,
  AuthnetReviewCustomer,
} from '../services/customerService';
import { formatBusinessDateTime } from '../lib/businessTime';

const useStyles = makeStyles({
  root: {
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  // Warning-state surface: white fill with a bright-orange (Pumpkin) border +
  // icon; neutral body text — the project's accent-not-fill warning language.
  note: {
    display: 'flex',
    alignItems: 'flex-start',
    ...shorthands.gap('12px'),
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderRadius('4px'),
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.border('1px', 'solid', tokens.colorPalettePumpkinBorderActive),
    color: tokens.colorNeutralForeground1,
  },
  noteIcon: {
    color: tokens.colorPalettePumpkinBorderActive,
    flexShrink: 0,
  },
  noteText: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('2px'),
  },
  empty: {
    ...shorthands.padding('32px'),
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('32px'),
  },
});

/** Format an ISO timestamp as a short business-timezone date-time, or an em dash. */
function formatFlaggedAt(iso: string | null): string {
  // authnet_synced_at is a real event timestamp — render date+time in the
  // business timezone.
  if (!iso) return '—';
  return formatBusinessDateTime(iso);
}

/**
 * Settings → Auth.Net linking review.
 *
 * Lists customers the link sweep parked as `ambiguous` — a profile exists for
 * the email but Auth.Net couldn't disambiguate, so we never auto-link. A human
 * resolves each by opening the customer and picking/merging the right AN
 * profile. Minimal by design (#278); a full inline-resolution UI is a separate
 * follow-up.
 */
export default function SettingsAuthnetReview() {
  const styles = useStyles();

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('setPageContext', {
      detail: { title: 'Auth.Net linking' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('setPageContext', { detail: null }));
    };
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.customers.authnetReview,
    queryFn: fetchAuthnetReviewCustomers,
  });

  const rows = data ?? [];

  const columns: ColumnConfig<AuthnetReviewCustomer>[] = [
    {
      columnKey: 'name',
      label: 'Customer',
      sortable: true,
      render: (c) => (
        <Link as={RouterLink as any} to={`/customers/${c.id}`}>
          {c.name || '(unnamed)'}
        </Link>
      ),
    },
    {
      columnKey: 'email',
      label: 'Email',
      sortable: true,
      render: (c) => <Text>{c.email || '—'}</Text>,
    },
    {
      columnKey: 'authnet_synced_at',
      label: 'Flagged',
      sortable: true,
      render: (c) => <Text>{formatFlaggedAt(c.authnet_synced_at)}</Text>,
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.note}>
        <Warning24Regular className={styles.noteIcon} />
        <div className={styles.noteText}>
          <Text weight="semibold">Needs manual Auth.Net linking</Text>
          <Text size={200}>
            An Auth.Net profile already exists for these customers' emails, but
            the gateway couldn't pin down which one — so they were left unlinked
            to avoid charging the wrong profile. Open each customer to pick or
            merge the correct profile.
          </Text>
        </div>
      </div>

      {isLoading && (
        <div className={styles.center}>
          <Spinner label="Loading review list…" />
        </div>
      )}

      {error && !isLoading && (
        <Text style={{ color: tokens.colorPaletteRedForeground1 }}>
          {error instanceof Error ? error.message : 'Failed to load the review list.'}
        </Text>
      )}

      {!isLoading && !error && rows.length === 0 && (
        <div className={styles.empty}>
          <Text>No customers need Auth.Net linking review. 🎉</Text>
        </div>
      )}

      {!isLoading && !error && rows.length > 0 && (
        <DataTable
          data={rows}
          columns={columns}
          keyField="id"
          hideableColumns
          columnVisibilityStorageKey="settings_authnet_review"
          itemsPerPage={25}
          primaryColumnKey="name"
          defaultSortColumn="authnet_synced_at"
          disableWrapper={true}
        />
      )}
    </div>
  );
}
